// src/components/profile/SectionRoadmap.jsx - PHASE 2.1: FIXED IMPORTS
import React, { useState, useMemo } from 'react';
import { Calendar, Users, Target, AlertTriangle } from 'lucide-react';
import RoadmapTimeline from '../RoadmapTimeline';

export default function SectionRoadmap({ 
  profileData, 
  roadmapData,
  milestones = [], 
  requirements = [],
  isCustomized = false,
  clientId, 
  useCaseId = 77 
}) {
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Use roadmap data from props, with fallbacks
  const dataSource = roadmapData || {
    timeframeMonths: 24,
    currentProjectMonth: 4,
    milestones: milestones.length > 0 ? milestones.map(m => ({
      ...m,
      status: m.status || 'planned'
    })) : [],
    valueInflections: [],
    dependencies: requirements
  };

  const {
    timeframeMonths,
    currentProjectMonth,
    milestones: apiMilestones,
    valueInflections,
    dependencies
  } = dataSource;

  // Process milestones for display
  const processedMilestones = useMemo(() => {
    const milestonesArray = Array.isArray(apiMilestones) ? apiMilestones : 
                           apiMilestones?.data || milestones || [];
    
    if (milestonesArray.length === 0) return [];
    
    return milestonesArray.map(milestone => ({
      label: milestone.label,
      months_from_start: milestone.months_from_start,
      description: milestone.value_note || 'Implementation milestone',
      value_note: milestone.value_note,
      dependencies: milestone.depends_on ? 
        (typeof milestone.depends_on === 'string' ? 
          milestone.depends_on.split(',').map(d => d.trim()) : 
          [milestone.depends_on]) : [],
      status: milestone.status || 'planned',
      owner: milestone.owner || '📊'
    }));
  }, [apiMilestones, milestones]);

  // Process value inflection points for display
  const valueInflectionPoints = useMemo(() => {
    if (valueInflections && valueInflections.length > 0) {
      return valueInflections.map(inflection => ({
        month: inflection.month,
        title: inflection.type === 'first_value' ? 'First Value' :
               inflection.type === 'break_even' ? 'Break-Even' :
               inflection.type === 'full_roi' ? 'Full ROI' : inflection.type,
        value: typeof inflection.value === 'number' ? 
          `$${(inflection.value / 1000).toFixed(0)}K` : 
          inflection.value,
        description: inflection.rationale || ''
      }));
    }
    
    // Fallback to computed values if no data
    return [
      { month: 6, title: 'First Value', value: '$25K', description: 'Initial pilot savings' },
      { month: 14, title: 'Break-Even', value: '$500K', description: 'Cumulative savings exceed investment' },
      { month: 24, title: 'Full ROI', value: '$1.2M', description: 'Annualized run-rate' }
    ];
  }, [valueInflections]);

  // Filter milestones based on selected filter
  const filteredMilestones = useMemo(() => {
    const now = currentProjectMonth || 0;
    switch (selectedFilter) {
      case 'next90d':
        return processedMilestones.filter(m => m.months_from_start >= now && m.months_from_start <= now + 3);
      case 'next6m':
        return processedMilestones.filter(m => m.months_from_start >= now && m.months_from_start <= now + 6);
      case 'beyond6m':
        return processedMilestones.filter(m => m.months_from_start > now + 6);
      default:
        return processedMilestones;
    }
  }, [processedMilestones, selectedFilter, currentProjectMonth]);

  // Display filtered milestones (Meeting Mode does NOT filter milestones in Roadmap tab)
  const displayedMilestones = filteredMilestones;

  // Process dependencies for display
  const processedDependencies = useMemo(() => {
    const deps = dependencies || requirements || [];
    if (!Array.isArray(deps)) return [];
    
    return deps.map(dep => ({
      type: dep.type || dep.req_type?.toLowerCase() || 'general',
      name: dep.name,
      details: dep.details,
      critical: dep.type === 'system' || dep.type === 'data' || 
                dep.req_type === 'Technical' || dep.req_type === 'Data'
    }));
  }, [dependencies, requirements]);

  // Send milestone to Action Board
  const handleSendToActionBoard = async (milestone) => {
    try {
      const response = await fetch('/api/v2/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: clientId,
          useCaseId: useCaseId,
          label: milestone.label,
          description: milestone.value_note || milestone.description,
          role: 'Project Manager',
          dueWindow: `Month ${milestone.months_from_start}`,
          priority: 'Medium',
          dependencies: milestone.dependencies || [],
          source: 'milestone'
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ "${milestone.label}" added to Action Board successfully!`);
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Failed to send to Action Board:', error);
      alert('❌ Failed to add action. Please try again.');
    }
  };

  // Loading state
  const loading = !roadmapData && !milestones && useCaseId;

  if (loading) {
    return (
      <section className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-32 bg-gray-100 rounded mb-4"></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="h-24 bg-gray-100 rounded"></div>
              <div className="h-24 bg-gray-100 rounded"></div>
              <div className="h-24 bg-gray-100 rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (processedMilestones.length === 0) {
    return (
      <section className="space-y-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No implementation milestones defined for this use case.</p>
          <p className="text-sm text-gray-500 mt-2">Roadmap will be developed during project planning.</p>
        </div>
      </section>
    );
  }

  // Main render
  return (
    <section id="roadmap" aria-labelledby="roadmap-title" className="scroll-mt-24">
      {/* PHASE 2.1: Implementation Timeline - Title & Context */}
      <div className="mb-4">
        <h2 id="roadmap-title" className="text-lg font-semibold text-[#18365E] flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Implementation Timeline
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {timeframeMonths} months total
          {currentProjectMonth > 0 && (
            <span className="text-blue-600"> • Currently in month {currentProjectMonth}</span>
          )}
        </p>
      </div>

      {/* PHASE 2.1: Visual Timeline Component */}
      {processedMilestones.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-2 mb-6">
          <RoadmapTimeline 
            milestones={processedMilestones}
            valueInflections={valueInflectionPoints}
            timeframeMonths={timeframeMonths}
            currentProjectMonth={currentProjectMonth}
          />
        </div>
      )}

      {/* PHASE 2.1: Implementation Milestones Cards */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mt-6 md:mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#18365E] flex items-center gap-2">
            <Users className="w-5 h-5" />
            Implementation Milestones
          </h3>
          
          {/* Filter Chips - Always visible */}
          <div className="flex items-center gap-2">
            {[
              { key: 'all', label: 'All', count: processedMilestones.length },
              { key: 'next90d', label: 'Next 90d', count: processedMilestones.filter(m => m.months_from_start >= (currentProjectMonth || 0) && m.months_from_start <= (currentProjectMonth || 0) + 3).length },
              { key: 'next6m', label: 'Next 6m', count: processedMilestones.filter(m => m.months_from_start >= (currentProjectMonth || 0) && m.months_from_start <= (currentProjectMonth || 0) + 6).length },
              { key: 'beyond6m', label: '>6m', count: processedMilestones.filter(m => m.months_from_start > (currentProjectMonth || 0) + 6).length }
            ].map(filter => (
              <button
                key={filter.key}
                onClick={() => setSelectedFilter(filter.key)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  selectedFilter === filter.key
                    ? 'bg-[#18365E] text-white border-[#18365E]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>
        </div>
        
        {/* Milestone Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedMilestones.map((milestone, idx) => (
            <div 
              key={idx} 
              className={`p-4 rounded-xl border-l-4 transition-all hover:shadow-md ${
                milestone.status === 'completed' ? 'border-green-500 bg-green-50 hover:bg-green-100' :
                milestone.status === 'in-progress' ? 'border-blue-500 bg-blue-50 hover:bg-blue-100' :
                'border-gray-300 bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  milestone.status === 'completed' ? 'bg-green-100 text-green-800' :
                  milestone.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  Month {milestone.months_from_start}
                </span>
                <span className="text-lg">{milestone.owner}</span>
              </div>
              
              <h4 className="font-semibold text-gray-900 text-sm mb-2">{milestone.label}</h4>
              
              {/* Show full details always */}
              <p className="text-xs text-gray-600 mb-3">{milestone.description}</p>
              
              {milestone.dependencies?.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-orange-600 mb-3">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Dependencies: {milestone.dependencies.length}</span>
                </div>
              )}

              {/* Send to Action Board Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendToActionBoard(milestone);
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#FF6E4C] text-white rounded-lg hover:bg-[#E55A38] transition-colors text-sm font-medium"
              >
                <Target className="w-4 h-4" />
                Send to Action Board
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Dependencies & Requirements - Always visible */}
      {processedDependencies.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mt-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Implementation Requirements
          </h3>
          
          <div className="space-y-3">
            {processedDependencies.map((req, idx) => (
              <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg border ${
                req.critical ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  req.critical ? 'bg-red-500' : 'bg-gray-400'
                }`} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{req.name}</div>
                  <div className="text-xs text-gray-600">{req.details}</div>
                  <div className="text-xs text-gray-500 capitalize mt-1">{req.type} requirement</div>
                </div>
                {req.critical && (
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded font-medium">
                    Critical
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}